/**
 * AppointmentService
 * Gestisce le chiamate API per la gestione appuntamenti:
 * - Creazione nuovi appuntamenti
 * - Recupero appuntamenti esistenti
 * - Modifica/reschedule appuntamenti
 * - Cancellazione appuntamenti
 */

import { BaseService } from './BaseService';
import logger from '../Logger';
// import AuditLogger from '../AuditLogger'; // Temporarily disabled for build

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Dati completi appuntamento dal backend
 */
export interface Appointment {
  APP_LID: string;
  APP_STATUS: string;
  APP_START_TIME: string;
  APP_END_TIME: string;
  APP_DATE: string;
  APP_CREATION_DATE: string;
  APP_PRICE?: number;
  RESOURCE_LID: string;
  ACTIVITY_LID: string;
  INSURANCE_LID: string;
  LOCATION_LID: string;
  USER_LID: string;
  USER_FIRST_NAME: string;
  USER_SECOND_NAME: string;
  USER_THIRD_NAME?: string;
  USER_ID_NUMBER: string;
  USER_LANDLINE_PHONE?: string;
  USER_MOBILE_PHONE?: string;
  USER_WORK_PHONE?: string;
  USER_EMAIL: string;
  USER_DATE_OF_BIRTH: string;
  USER_GENDER: string;
  COMMUNICATION_PREFERENCES?: string;
  USER_PRIVACY?: string;
  USER_PRIVACY_PROMOTIONS?: string;
  APP_REMINDER?: boolean;
  RESOURCE_DESCRIPTION?: string;
  ACTIVITY_NAME?: string;
  ACTIVITY_PREPARATION?: string;
  EXAM_ID?: number;
  ACTIVITY_NUMERIC_ID?: number;
}

/**
 * Dati per creazione nuovo appuntamento
 */
export interface AppointmentAddRequest {
  app_tid?: string;
  app_action?: string;
  app_start_time: string;
  app_end_time: string;
  app_date: string;
  resource_lid: string;
  activity_lid: string;
  insurance_lid: string;
  location_lid: string;
  user_lid: string;
  user_tid?: string;
  user_first_name: string;
  user_second_name: string;
  user_third_name?: string;
  user_id_number: string;
  user_landline_phone?: string;
  user_mobile_phone?: string;
  user_email: string;
  user_date_of_birth: string;
  user_place_of_birth?: string;
  user_gender: string;
  user_privacy?: string;
  user_city?: string;
  user_province?: string;
  user_country?: string;
  user_zip_code?: string;
  user_address?: string;
  app_notes?: string;
}

/**
 * Risultato creazione appuntamento
 */
export interface AppointmentAddResult {
  AppId: string;
  UserId: string;
  Result: 'OK' | 'KO';
  ErrorMessage?: string;
}

/**
 * Dati per modifica appuntamento esistente
 */
export interface AppointmentUpdateRequest {
  app_lid: string;
  new_availability_lid: string;
  new_app_clinicdepartmentid: string;
  new_app_start_time: string;
  new_app_end_time: string;
  new_app_date: string;
}

/**
 * Risultato modifica appuntamento
 */
export interface AppointmentUpdateResult {
  Result: 'OK' | 'KO';
  ErrorMessage?: string;
}

/**
 * Dati per cancellazione appuntamento
 */
export interface AppointmentDeleteRequest {
  Id: string;
}

/**
 * Risultato cancellazione appuntamento
 */
export interface AppointmentDeleteResult {
  Result: 'OK' | 'KO';
  ErrorMessage?: string;
  AppId: string;
}

/**
 * Formato risposta API standard del backend
 */
interface ApiResponse<T> {
  Return: T;
  Msg?: string;
  Result: 'OK' | 'KO';
}

// ============================================================================
// SERVICE CLASS
// ============================================================================

class AppointmentService extends BaseService {
  private static instance: AppointmentService;

  private constructor() {
    super();
  }

  /**
   * Ottiene istanza singleton del servizio
   */
  static getInstance(): AppointmentService {
    if (!AppointmentService.instance) {
      AppointmentService.instance = new AppointmentService();
      logger.info('AppointmentService initialized');
    }
    return AppointmentService.instance;
  }

  // ==========================================================================
  // PUBLIC METHODS
  // ==========================================================================

  /**
   * Crea un nuovo appuntamento
   * @param data - Dati completi appuntamento
   * @returns Risultato con ID appuntamento creato
   */
  async addAppointment(data: AppointmentAddRequest): Promise<AppointmentAddResult> {
    try {
      logger.info('AppointmentService.addAppointment', {
        activityId: data.activity_lid,
        date: data.app_date,
        userId: data.user_lid
      });

      // Validazione dati obbligatori
      this.validateAppointmentData(data);

      const response = await this.post<ApiResponse<AppointmentAddResult>>(
        'clientAPI/addappointment',
        data
      );

      if (response.Result === 'KO' || response.Return?.Result === 'KO') {
        const errorMsg = response.Return?.ErrorMessage || response.Msg || 'Errore durante la creazione dell\'appuntamento';
        // Passiamo il messaggio anche nei details: per gli errori 400 (VALIDATION) l'ErrorHandler
        // legge il messaggio user-friendly da details.message, altrimenti mostrerebbe un testo generico.
        throw this.createError(errorMsg, 400, { message: errorMsg });
      }

      // VERIFICA POST-PRENOTAZIONE: Confermiamo che l'appuntamento sia stato realmente salvato
      // Questo previene il caso in cui l'API restituisce OK ma l'appuntamento non è stato persistito
      const appointmentId = response.Return.AppId;
      if (appointmentId) {
        try {
          const verifyResponse = await this.get<ApiResponse<Appointment[]>>(
            `clientAPI/getappointments?app_lid=${appointmentId}`
          );

          const appointmentExists = verifyResponse.Result === 'OK' &&
            verifyResponse.Return &&
            verifyResponse.Return.length > 0;

          if (!appointmentExists) {
            logger.error('CRITICAL: Appointment not found after creation', {
              appointmentId,
              verifyResponse: verifyResponse.Result
            });
            throw this.createError(
              'La prenotazione potrebbe non essere stata salvata correttamente. Verifica nella sezione Appuntamenti o riprova.',
              500
            );
          }

          logger.info('Appointment verified in database', { appointmentId });
        } catch (verifyError) {
          // Se la verifica fallisce ma la creazione ha avuto successo, loggiamo ma non falliamo
          // L'utente vedrà comunque l'appuntamento se esiste
          logger.warn('Could not verify appointment existence, proceeding anyway', {
            appointmentId,
            error: verifyError
          });
        }
      }

      logger.info('Appointment created successfully', {
        appointmentId: response.Return.AppId
      });

      return response.Return;

    } catch (error) {
      logger.error('Failed to create appointment', error);
      throw error;
    }
  }

  /**
   * Recupera appuntamenti di un paziente
   * @param patientId - ID paziente (user_lid)
   * @returns Array di appuntamenti
   */
  async getAppointments(patientId: string): Promise<Appointment[]> {
    try {
      logger.debug('AppointmentService.getAppointments', { patientId });

      if (!patientId) {
        throw this.createError('Patient ID è obbligatorio', 400);
      }

      const response = await this.get<ApiResponse<Appointment[]>>(
        `clientAPI/getappointments?user_lid=${patientId}`
      );

      if (response.Result === 'KO') {
        throw this.createError(
          response.Msg || 'Errore durante il recupero degli appuntamenti',
          400
        );
      }

      const appointments = response.Return || [];
      logger.info('Appointments loaded successfully', {
        count: appointments.length,
        patientId
      });

      return appointments;

    } catch (error) {
      logger.error('Failed to load appointments', error);
      throw error;
    }
  }

  /**
   * Recupera un singolo appuntamento per ID
   * @param appointmentId - ID appuntamento
   * @returns Appuntamento
   */
  async getAppointmentById(appointmentId: string): Promise<Appointment | null> {
    try {
      logger.debug('AppointmentService.getAppointmentById', { appointmentId });

      if (!appointmentId) {
        throw this.createError('Appointment ID è obbligatorio', 400);
      }

      const response = await this.get<ApiResponse<Appointment[]>>(
        `clientAPI/getappointments?app_lid=${appointmentId}`
      );

      if (response.Result === 'KO') {
        throw this.createError(
          response.Msg || 'Errore durante il recupero dell\'appuntamento',
          400
        );
      }

      const appointments = response.Return || [];
      return appointments.length > 0 ? appointments[0] : null;

    } catch (error) {
      logger.error('Failed to load appointment', error);
      throw error;
    }
  }

  /**
   * Modifica/Reschedule un appuntamento esistente
   * @param data - Nuovi dati appuntamento
   * @returns Risultato operazione
   */
  async rescheduleAppointment(data: AppointmentUpdateRequest): Promise<AppointmentUpdateResult> {
    try {
      logger.info('AppointmentService.rescheduleAppointment', {
        appointmentId: data.app_lid,
        newDate: data.new_app_date
      });

      // Validazione
      if (!data.app_lid || !data.new_availability_lid || !data.new_app_date) {
        throw this.createError('Dati mancanti per la modifica dell\'appuntamento', 400);
      }

      const response = await this.post<ApiResponse<AppointmentUpdateResult>>(
        'clientAPI/rescheduleAppointment',
        data
      );

      if (response.Result === 'KO' || response.Return?.Result === 'KO') {
        const errorMsg = response.Return?.ErrorMessage || response.Msg || 'Errore durante la modifica dell\'appuntamento';
        throw this.createError(errorMsg, 400);
      }

      // Audit logging
      // Audit logging temporarily disabled

      logger.info('Appointment rescheduled successfully', {
        appointmentId: data.app_lid
      });

      return response.Return;

    } catch (error) {
      logger.error('Failed to reschedule appointment', error);
      throw error;
    }
  }

  /**
   * Cancella un appuntamento
   * @param appointmentId - ID appuntamento da cancellare
   * @returns Risultato operazione
   */
  async deleteAppointment(appointmentId: string): Promise<AppointmentDeleteResult> {
    try {
      logger.info('AppointmentService.deleteAppointment', { appointmentId });

      if (!appointmentId) {
        throw this.createError('Appointment ID è obbligatorio', 400);
      }

      const response = await this.post<ApiResponse<AppointmentDeleteResult>>(
        'clientAPI/deleteappointment',
        { Id: appointmentId }
      );

      if (response.Result === 'KO' || response.Return?.Result === 'KO') {
        const errorMsg = response.Return?.ErrorMessage || response.Msg || 'Errore durante la cancellazione dell\'appuntamento';
        throw this.createError(errorMsg, 400);
      }

      // Audit logging
      // Audit logging temporarily disabled

      logger.info('Appointment deleted successfully', { appointmentId });

      return response.Return;

    } catch (error) {
      logger.error('Failed to delete appointment', error);
      throw error;
    }
  }

  // ==========================================================================
  // HELPER METHODS
  // ==========================================================================

  /**
   * Valida dati appuntamento prima della creazione
   */
  private validateAppointmentData(data: AppointmentAddRequest): void {
    const requiredFields = [
      'app_start_time',
      'app_end_time',
      'app_date',
      'resource_lid',
      'activity_lid',
      'insurance_lid',
      'location_lid',
      'user_lid',
      'user_first_name',
      'user_second_name',
      'user_id_number',
      'user_email',
      'user_date_of_birth',
      'user_gender'
    ];

    for (const field of requiredFields) {
      if (!data[field as keyof AppointmentAddRequest]) {
        throw this.createError(`Campo obbligatorio mancante: ${field}`, 400);
      }
    }
  }

  /**
   * Formatta appuntamento per display
   */
  formatAppointmentDisplay(appointment: Appointment): string {
    const date = new Date(appointment.APP_DATE).toLocaleDateString('it-IT');
    const startTime = appointment.APP_START_TIME;
    const endTime = appointment.APP_END_TIME;
    return `${date} alle ${startTime} - ${endTime}`;
  }

  /**
   * Controlla se un appuntamento è modificabile
   * (es. non può essere modificato se troppo vicino alla data)
   */
  isAppointmentEditable(appointment: Appointment, minHoursThreshold: number = 48): boolean {
    const appointmentDateTime = new Date(`${appointment.APP_DATE}T${appointment.APP_START_TIME}`);
    const now = new Date();
    const hoursDifference = (appointmentDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursDifference >= minHoursThreshold;
  }

  /**
   * Raggruppa appuntamenti per stato
   */
  groupAppointmentsByStatus(appointments: Appointment[]): {
    upcoming: Appointment[];
    past: Appointment[];
  } {
    const now = new Date();

    return appointments.reduce(
      (groups, appointment) => {
        const appointmentDate = new Date(`${appointment.APP_DATE}T${appointment.APP_START_TIME}`);

        if (appointmentDate >= now) {
          groups.upcoming.push(appointment);
        } else {
          groups.past.push(appointment);
        }

        return groups;
      },
      { upcoming: [] as Appointment[], past: [] as Appointment[] }
    );
  }
}

// Export singleton instance
export default AppointmentService.getInstance();
